import { useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import driverPlaceholder from 'assets/images/driver-placeholder.svg'
import { INoteFormValues } from 'features/Trips/types'
import { TDriverDocumentCategory, TripDriverRsvpNoteStatus, TripV2Driver } from 'gql/generated/graphql'
import { safeFormatMessage } from 'utils/intl'
import { getShortId } from 'utils/string'
import { GET_TRIP_DRIVER } from 'features/Trips/queries'
import { useQuery } from '@apollo/client'
import { ErrorMessage } from 'components/v2/ErrorMessage'

const DriverDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

const DriverDetailsPhoto = styled.img`
  border-radius: 24px;
  height: 96px;
  width: 96px;
  margin: 0 auto 4px;
`

const DriverName = styled.div`
  text-align: center;
  font-size: 22px;
  font-weight: 500;
  color: #28265d;
`

const DriverId = styled.div`
  color: #b0b0b0;
  font-size: 18px;
  font-weight: 400;
`

const SelectDriverRSVPStatusContainer = styled.div`
  display: flex;
  justify-content: center;
`

const SelectDriverRSVPStatus = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #916ee8;
  border-radius: 8px;
  overflow: hidden;
  width: 300px;

  *:first-child {
    border-right: 1px solid #916ee8;
  }
`

const SelectDriverRSVPStatusButton = styled.button<{ isSelected: boolean }>`
  background-color: ${({ isSelected }): string => (isSelected ? '#916EE8' : '#fff')};
  color: ${({ isSelected }): string => (isSelected ? '#fff' : '#916EE8')};
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  padding: 12px 24px;
  cursor: pointer;
  border: none;
`

const ErrorMessageContainer = styled.div`
  text-align: center;
`

const driverRsvpNoteStatusMessages = defineMessages({
  [TripDriverRsvpNoteStatus.Confirmed]: {
    id: 'trip.drawer.notes.driverRSVPNote.status.confirmed',
    defaultMessage: 'Confirmed'
  },
  [TripDriverRsvpNoteStatus.Declined]: {
    id: 'trip.drawer.notes.driverRSVPNote.status.declined',
    defaultMessage: 'Declined'
  }
})

interface IDriverRSVPNoteProps {
  driver: TripV2Driver
}

export const DriverRSVPNote = ({ driver }: IDriverRSVPNoteProps): ReactElement => {
  const intl = useIntl()
  const { data } = useQuery(GET_TRIP_DRIVER, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        id: driver.id
      }
    }
  })
  const { values, setFieldValue, touched, errors } = useFormikContext<INoteFormValues>()

  const handleRSVPStatusChange = (status: TripDriverRsvpNoteStatus) => () => {
    setFieldValue('driverRSVPStatus', status)
  }

  const driverDocuments = data?.tdriver?.documents ?? []
  const driverPhoto = driverDocuments.find(({ category }) => category === TDriverDocumentCategory.Photo)
  const photoPath = driverPhoto?.document.path || driverPlaceholder

  return (
    <>
      <DriverDetails>
        <DriverDetailsPhoto src={photoPath} />
        <DriverName>
          {driver?.firstName} {driver?.lastName}
        </DriverName>
        <DriverId>ID: {getShortId(driver?.id)}</DriverId>
      </DriverDetails>
      <SelectDriverRSVPStatusContainer>
        <div>
          <SelectDriverRSVPStatus>
            <SelectDriverRSVPStatusButton
              type="button"
              isSelected={values.driverRSVPStatus === TripDriverRsvpNoteStatus.Confirmed}
              onClick={handleRSVPStatusChange(TripDriverRsvpNoteStatus.Confirmed)}
            >
              {safeFormatMessage({
                intl,
                messages: driverRsvpNoteStatusMessages,
                key: TripDriverRsvpNoteStatus.Confirmed
              })}
            </SelectDriverRSVPStatusButton>
            <SelectDriverRSVPStatusButton
              type="button"
              isSelected={values.driverRSVPStatus === TripDriverRsvpNoteStatus.Declined}
              onClick={handleRSVPStatusChange(TripDriverRsvpNoteStatus.Declined)}
            >
              {safeFormatMessage({
                intl,
                messages: driverRsvpNoteStatusMessages,
                key: TripDriverRsvpNoteStatus.Declined
              })}
            </SelectDriverRSVPStatusButton>
          </SelectDriverRSVPStatus>
          {touched.driverRSVPStatus && errors.driverRSVPStatus && (
            <ErrorMessageContainer>
              <ErrorMessage>{errors.driverRSVPStatus}</ErrorMessage>
            </ErrorMessageContainer>
          )}
        </div>
      </SelectDriverRSVPStatusContainer>
    </>
  )
}
